import eventServiceApi from "@/Services/event-service-api";
import {
  getPdfSimpliBaseURL,
  getSignSimpliBaseURL,
  getResumeBuildBaseURL,
} from "@/Services/base-url/api-constants";
import { DomainName, EMPTY_GUID } from "./constant";
import { currentDomain, getTenantId } from "./general-config";
import { normalizeGuid, readCookie, isTempUserGuid } from "./helper";
import {
  fetchUserProfileAbridged,
  readProfileString,
  resolveProfileFirstName,
  type UserProfileRecord,
} from "./userProfile";
import { ROUTE_CONSTANT } from "@/routing/route-constants";

/**
 * SendGrid template for the "your song is being created" mail.
 *
 * TODO: fill in once the template is created. While this is empty the send is
 * skipped so the page still renders — nothing else needs to change.
 */
const SONG_ORDER_EMAIL_TEMPLATE_ID = "";

const SONG_ORDER_EMAIL_SUBJECT = "Your song is being created!";
const FROM_EMAIL_NAME = "##Simpli";

/** Portal codes the event service routes on. */
const BasePortalCD = {
  PS: "PS",
  SS: "SS",
  LSEN: "LSEN",
  WKSM: "WKSM",
  WSRB: "WSRB",
  PSSC: "PSSC",
} as const;

interface PortalConfig {
  portalBaseUrl: string;
  senderNamePrefix: string;
  portal: string;
}

function getPortalConfig(): PortalConfig {
  switch (currentDomain()) {
    case DomainName.SIGNSIMPLI:
      return {
        portalBaseUrl: getSignSimpliBaseURL,
        senderNamePrefix: "Sign",
        portal: "SignSimpli",
      };
    case DomainName.RESUMEBUILD:
      return {
        portalBaseUrl: getResumeBuildBaseURL,
        senderNamePrefix: "Resume",
        portal: "ResumeBuild",
      };
    default:
      return {
        portalBaseUrl: getPdfSimpliBaseURL,
        senderNamePrefix: "Pdf",
        portal: "PDFSimpli",
      };
  }
}

function currentPortalCD(locale?: string): string {
  const host = window.location.host;
  let portal: string = BasePortalCD.PS;

  if (host.indexOf("signsimpli") > -1) {
    portal = BasePortalCD.SS;
  } else if (host.indexOf("resumebuild") > -1) {
    portal = BasePortalCD.WSRB;
  } else if (host.indexOf("legalsimpli") > -1) {
    portal = BasePortalCD.LSEN;
  } else if (host.indexOf("worksimpli") > -1) {
    portal = BasePortalCD.WKSM;
  }

  if (locale && locale !== "en") {
    portal = portal + locale.toUpperCase();
  }
  return portal;
}

function getLocaleSegment(): string {
  const locale = (localStorage.getItem("i18nextLng") || "en").trim();
  return locale === "en" ? "" : `/${locale}`;
}

/** Deep link back to the journey page for this order. */
function buildTrackSongLink(
  portalBaseUrl: string,
  docId: string,
  userId: string,
): string {
  const base = portalBaseUrl.replace(/\/$/, "");
  const path = ROUTE_CONSTANT.TRACK_SONG.replace(
    "/app",
    `/app${getLocaleSegment()}`,
  );
  return `${base}${path}?docId=${encodeURIComponent(docId)}&uId=${encodeURIComponent(userId)}`;
}

export interface SongOrderMailPayload {
  docId: string;
  userId: string;
  /** Person the song is for — used in the mail copy. */
  recipientName?: string;
  occasion?: string;
  musicStyle?: string;
}

/** sessionStorage key so a page refresh does not re-send the same mail. */
function sentKey(docId: string): string {
  return `songOrderMailSent:${docId}`;
}

function hasAlreadySent(docId: string): boolean {
  try {
    return sessionStorage.getItem(sentKey(docId)) === "true";
  } catch {
    return false;
  }
}

function markAsSent(docId: string): void {
  try {
    sessionStorage.setItem(sentKey(docId), "true");
  } catch {
    // Private-mode storage failures must not break the page.
  }
}

/** Abridged profile exposes `fullName`; the template wants a first name. */
function resolveFirstName(profile: UserProfileRecord | null): string {
  return resolveProfileFirstName(profile);
}

function buildEmailMeta(
  profile: UserProfileRecord | null,
  payload: SongOrderMailPayload,
  userGuid: string,
  portalCD: string,
  config: PortalConfig,
): Record<string, string> {
  const trackLink = buildTrackSongLink(config.portalBaseUrl, payload.docId, userGuid);
  const email = readProfileString(profile, "email");

  return {
    accountid: readProfileString(profile, "accountId"),
    FirstName: resolveFirstName(profile),
    PortalCD: portalCD,
    currenturl: trackLink,
    toemail: email,
    useremail: email,
    sendername: FROM_EMAIL_NAME.replace("##", config.senderNamePrefix),
    emailsubject: SONG_ORDER_EMAIL_SUBJECT,
    disablebcc: "true",
    isvalidcheck: "true",
    portal: config.portal,
    redirection_link: trackLink,
    // Song specifics for the template body.
    songfor: payload.recipientName ?? "",
    occasion: payload.occasion ?? "",
    genre: payload.musicStyle ?? "",
  };
}

/**
 * Fires the order-confirmation mail once per docId per session, as an
 * `emailengine` event — same delivery path as WorkSimpli.Login's welcome mail.
 * Failures are logged, never surfaced: the mail is a side effect of landing on
 * the page, not something the user is waiting on.
 */
export async function fireSongOrderMailEvent(
  payload: SongOrderMailPayload,
): Promise<void> {
  const { docId } = payload;
  if (!docId || hasAlreadySent(docId)) {
    return;
  }

  if (!SONG_ORDER_EMAIL_TEMPLATE_ID) {
    console.info(
      "Song order mail skipped — SONG_ORDER_EMAIL_TEMPLATE_ID is not set yet.",
    );
    return;
  }

  const userGuid = normalizeGuid(payload.userId || readCookie("userguid"));
  if (!userGuid) {
    console.warn("Song order mail skipped — no user guid available.");
    return;
  }

  // Marked before the request so a StrictMode double-invoke cannot double-send.
  markAsSent(docId);

  try {
    const profile = isTempUserGuid(userGuid)
      ? null
      : await fetchUserProfileAbridged(userGuid);
    const config = getPortalConfig();
    const portalCD = currentPortalCD();

    const emailDelivery = {
      userGuid,
      templateId: SONG_ORDER_EMAIL_TEMPLATE_ID,
      emailMetaData: buildEmailMeta(profile, payload, userGuid, portalCD, config),
      envelopeId: EMPTY_GUID,
      isAttachment: false,
      tenantId: getTenantId(),
    };

    const eventType =
      portalCD.indexOf(BasePortalCD.WKSM) > -1 || portalCD === BasePortalCD.PSSC
        ? BasePortalCD.PS
        : portalCD;

    // Silent: the mail is a background side effect of landing on the page.
    await eventServiceApi.postDataToEventService(
      emailDelivery,
      eventType,
      "Email",
      "emailengine",
      true,
    );
  } catch (error) {
    console.error("Failed to send song order mail:", error);
  }
}
